/**
 * Sticky Planet - Main Entry Point
 * 
 * Initializes the Phaser game instance with all scenes and platform SDK.
 * This is the first file executed when the game loads.
 */

import Phaser from 'phaser';
import { GameConfig } from './config/GameConfig';
import { BootScene } from './scenes/BootScene';
import { PreloadScene } from './scenes/PreloadScene';
import { MenuScene } from './scenes/MenuScene';
import { GameScene } from './scenes/GameScene';
import { GameOverScene } from './scenes/GameOverScene';
import { PlatformManager } from './sdk/PlatformManager';

/**
 * Initialize the game with all scenes
 */
function initGame(): Phaser.Game {
  // Add all scenes to config
  const config: Phaser.Types.Core.GameConfig = {
    ...GameConfig,
    scene: [
      BootScene,
      PreloadScene,
      MenuScene,
      GameScene,
      GameOverScene
    ]
  };
  
  // Create game instance
  const game = new Phaser.Game(config);
  
  // Store game reference globally for SDK access
  (window as Window & { game?: Phaser.Game }).game = game;
  
  return game;
}

/**
 * Handle visibility change (pause when tab hidden)
 */
function setupVisibilityHandler(game: Phaser.Game): void {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      // Pause game when tab is hidden
      game.scene.scenes.forEach(scene => {
        if (scene.scene.isActive()) {
          scene.scene.pause();
        }
      });
    } else {
      // Resume game when tab is visible
      game.scene.scenes.forEach(scene => {
        if (scene.scene.isPaused()) {
          scene.scene.resume();
        }
      });
    }
  });
}

/**
 * Handle resize events
 */
function setupResizeHandler(game: Phaser.Game): void {
  let resizeTimeout: number | null = null;
  
  window.addEventListener('resize', () => {
    // Debounce resize events
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }
    
    resizeTimeout = window.setTimeout(() => {
      game.scale.resize(window.innerWidth, window.innerHeight);
    }, 100);
  });
}

/**
 * Handle orientation change (mobile)
 */
function setupOrientationHandler(): void {
  window.addEventListener('orientationchange', () => {
    // Small delay to let browser complete orientation change
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 200);
  });
}

/**
 * Prevent default touch behaviors
 */
function preventDefaultBehaviors(): void {
  // Prevent pull-to-refresh on mobile
  document.body.addEventListener('touchmove', (e) => {
    if (e.target === document.body) {
      e.preventDefault();
    }
  }, { passive: false });
  
  // Prevent double-tap zoom
  let lastTouchEnd = 0;
  document.body.addEventListener('touchend', (e) => {
    const now = Date.now();
    if (now - lastTouchEnd <= 300) {
      e.preventDefault();
    }
    lastTouchEnd = now;
  }, { passive: false });
}

/**
 * Main initialization
 */
async function main(): Promise<void> {
  try {
    // Prevent default mobile behaviors
    preventDefaultBehaviors();
    
    // Initialize platform SDK first
    await PlatformManager.getInstance().init();
    
    // Notify SDK that game is loading
    PlatformManager.getInstance().gameLoadingStart();
    
    // Create game instance
    const game = initGame();
    
    // Setup event handlers
    setupVisibilityHandler(game);
    setupResizeHandler(game);
    setupOrientationHandler();
    
    // Log successful initialization
    console.log('[Main] Sticky Planet initialized successfully');
    
  } catch (error) {
    console.error('[Main] Failed to initialize game:', error);
    
    // Show error to user
    const loadingScreen = document.getElementById('loading-screen');
    if (loadingScreen) {
      loadingScreen.innerHTML = `
        <div style="color: #ff6b6b; text-align: center; padding: 2rem;">
          <h2>Failed to Load Game</h2>
          <p>Please refresh the page to try again.</p>
          <button onclick="location.reload()" style="
            margin-top: 1rem;
            padding: 0.5rem 1rem;
            background: #00d4ff;
            border: none;
            border-radius: 4px;
            color: white;
            font-size: 1rem;
            cursor: pointer;
          ">Retry</button>
        </div>
      `;
    }
  }
}

// Start the game when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', main);
} else {
  main();
}
